import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { errorSideEffects, loadingSideEffects, RTKSideEffectSchema, successSideEffects, updatingSideEffects } from "../..";
import { APIErrorResponseSchema } from "../../../actions";
import { Toastify } from "../../../../components/Toaster";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../../services/analytics/tracking";

// The API splits IP restrictions into two independently-toggled scopes:
//   manage — IsEnabled + AllowedIPs/DeniedIPs, enforced on the Management APIs
//   auth   — IsAuthEnabled + AuthAllowedIPs/AuthDeniedIPs, enforced on the Authentication APIs
// A PUT writes only the scopes whose fields it names, so each scope is saved on its own.
export type IpScope = "manage" | "auth";

export interface IpAccessConfig {
  IsEnabled?: boolean;
  AllowedIPs?: string[] | null;
  DeniedIPs?: string[] | null;
  IsAuthEnabled?: boolean;
  AuthAllowedIPs?: string[] | null;
  AuthDeniedIPs?: string[] | null;
  [key: string]: unknown;
}

// Payload dispatched by the UI to persist one scope's fields. `config` must carry ONLY
// the fields of `scope` — naming a field of the other scope would write it too. An
// omitted list keeps its stored entries; `[]` clears them; never send `[""]`.
export interface UpdateIpAccessManagementPayload {
  scope: IpScope;
  // Correlates the outcome back to the section that dispatched it. The two sections
  // can have saves in flight at once, and the shared `isUpdating` flag cannot tell
  // them apart — nor does it reliably re-rise between two overlapping mutations.
  requestId: string;
  config: IpAccessConfig;
  successMessage?: string;
}

export interface IpAccessMutationResult {
  requestId: string;
  scope: IpScope;
  ok: boolean;
  error?: string;
}

export interface IpAccessManagementState extends RTKSideEffectSchema, APIErrorResponseSchema, MutationTrackingState {
  Data: IpAccessConfig | null;
  IsEnabled: boolean;
  IsAuthEnabled: boolean;
  // Outcome of the most recently settled mutation, tagged with the id of the request
  // that produced it. A section settles when this names its own in-flight request.
  lastResult: IpAccessMutationResult | null;
}

const initialState: IpAccessManagementState = {
  isLoading: true,
  isUpdating: false,
  isSuccess: false,
  isError: false,
  error: null,
  Data: null,
  IsEnabled: false,
  IsAuthEnabled: false,
  lastResult: null
};

// Field names per scope, mirroring SCOPE_FIELDS in IpScopeSection.
const SCOPE_KEYS: Record<IpScope, string[]> = {
  manage: ["IsEnabled", "AllowedIPs", "DeniedIPs"],
  auth: ["IsAuthEnabled", "AuthAllowedIPs", "AuthDeniedIPs"]
};

// `mergeScope` is for mutation responses. A PUT echoes the WHOLE config, but that echo is a
// snapshot taken when the request was served — and `takeEvery` lets both scopes write at once, so
// a slow response can carry pre-write values for the scope it did not touch. Taking only the
// acted-on scope's three keys keeps a late reply from reverting the other section's fresh list.
// A GET replaces outright: it is the canonical full config, and merging would keep entries the
// server no longer has. Both switches are always resolved on a GET, so a missing one means the
// read failed rather than "never configured" — coerce and move on.
const applyConfig = (state: IpAccessManagementState, config: IpAccessConfig | null, mergeScope?: IpScope) => {
  const scoped = mergeScope && config ? Object.fromEntries(SCOPE_KEYS[mergeScope].filter((key) => key in config).map((key) => [key, config[key]])) : config || {};
  const next: IpAccessConfig = mergeScope ? { ...(state.Data || {}), ...scoped } : scoped;
  state.Data = next;
  state.IsEnabled = Boolean(next.IsEnabled);
  state.IsAuthEnabled = Boolean(next.IsAuthEnabled);
};

const ipAccessManagementSlice = createSlice({
  name: "ipAccessManagement",
  initialState,
  reducers: {
    getIpAccessManagementRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getIpAccessManagementSuccess(state, action: PayloadAction<IpAccessConfig>) {
      Object.assign(state, successSideEffects);
      applyConfig(state, action.payload);
    },
    getIpAccessManagementError(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
    },
    updateIpAccessManagementRequest(state, action: PayloadAction<UpdateIpAccessManagementPayload>) {
      // getRequestTrackingState parks the dispatched action's trackingMeta on the slice so the
      // analytics mapper can emit once the mutation settles. It holds ONE pending request, so
      // when both scopes save at the same moment only the later one is reported — the sections'
      // own `lastResult`/`requestId` correlation is unaffected, this is analytics only.
      Object.assign(state, updatingSideEffects, getRequestTrackingState(action));
      // Clear any earlier outcome so a section never settles twice on a stale result.
      if (state.lastResult?.scope === action.payload.scope) state.lastResult = null;
      state.error = null;
    },
    updateIpAccessManagementSuccess(state, action: PayloadAction<{ requestId: string; scope: IpScope; data: IpAccessConfig; successMessage?: string }>) {
      Object.assign(state, successSideEffects, getOutcomeTrackingState(state, "success"));
      applyConfig(state, action.payload.data, action.payload.scope);
      state.lastResult = { requestId: action.payload.requestId, scope: action.payload.scope, ok: true };
      if (action.payload.successMessage) {
        Toastify("success", action.payload.successMessage);
      }
    },
    updateIpAccessManagementError(state, action: PayloadAction<{ requestId: string; scope: IpScope; error: string }>) {
      Object.assign(state, errorSideEffects, getOutcomeTrackingState(state, "error"));
      state.error = action.payload.error;
      state.lastResult = { requestId: action.payload.requestId, scope: action.payload.scope, ok: false, error: action.payload.error };
      // The API's Description names the offending entries and the lists holding them,
      // so it is surfaced verbatim rather than replaced with a generic message.
      Toastify("error", action.payload.error);
    }
  }
});

export const {
  getIpAccessManagementRequest,
  getIpAccessManagementSuccess,
  getIpAccessManagementError,
  updateIpAccessManagementRequest,
  updateIpAccessManagementSuccess,
  updateIpAccessManagementError
} = ipAccessManagementSlice.actions;

export default ipAccessManagementSlice.reducer;
